import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { taskOutputSchema } from "./shared";

/**
 * Create Subtask Step
 *
 * Creates a full task parented under an existing task (parentTaskId), with an
 * optional org-scoped task type key. Subtasks of a typed parent feed the
 * parent's expected-subtask completion gating (rollupTaskPhase).
 */
export const createSubtaskStep = defineStepConfig({
  type: "create_subtask",
  name: "Create Subtask",
  description:
    "Create a subtask under an existing task, with an optional task type and assignee",
  category: "Tasks",

  inputSchema: z
    .object({
      parentTaskId: z.uuid().describe("The task this subtask is created under"),
      title: z.string().describe("Title of the subtask"),
      description: z
        .string()
        .optional()
        .describe("Detailed subtask description"),
      priority: z
        .enum(["low", "medium", "high", "urgent"])
        .optional()
        .describe("Subtask priority (defaults to medium)"),
      taskTypeKey: z
        .string()
        .optional()
        .describe(
          "Org-scoped task type key for the subtask (e.g. 'verify-receipt'). Wire from an upstream step; overrides the config default.",
        ),
      anchorEntity: SystemEntitySchema.optional().describe(
        "Business entity to anchor the subtask to (requires a subtask type declaring a matching anchorEntityType). Fails if another open task already holds the (type, anchor) slot.",
      ),
      anchorEntityType: z
        .nativeEnum(SystemEntity)
        .optional()
        .describe(
          "Anchor entity type — an alternative to `anchorEntity` for when you only have a bare entity id (paired with `anchorEntityId`). Ignored if `anchorEntity` is provided.",
        ),
      anchorEntityId: z
        .uuid()
        .optional()
        .describe(
          "Anchor entity id — an alternative to `anchorEntity` (paired with `anchorEntityType`). Ignored if `anchorEntity` is provided.",
        ),
      assigneeType: z
        .enum(["user", "agent"])
        .optional()
        .describe("Type of assignee"),
      assigneeUserId: z
        .uuid()
        .optional()
        .describe("User ID when assigneeType is user"),
      assigneeAgentDefinitionId: z
        .uuid()
        .optional()
        .describe("Agent definition ID when assigneeType is agent"),
      dueDate: z.iso
        .datetime()
        .optional()
        .describe("Due date as ISO datetime string"),
      metadata: z
        .record(z.string(), z.unknown())
        .optional()
        .describe("Arbitrary metadata to attach to the subtask"),
      input: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
          "Typed payload required when the selected task type declares an input schema. Field sockets `taskInput.<name>` overlay this bag.",
        ),
      tags: z.array(z.string()).optional().describe("Tags for the subtask"),
      taskNamespaceId: z
        .uuid()
        .optional()
        .describe(
          "Namespace to create the subtask in. Overrides the step's configured namespace. A selected task type with a live default namespace still wins; otherwise the org default is used.",
        ),
    })
    .catchall(z.unknown()),

  outputSchema: z.object({
    task: taskOutputSchema,
    taskId: z.uuid().describe("The created subtask's id (flat, for wiring)"),
  }),

  configSchema: z
    .object({
      taskTypeKey: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe(
          "Default org-scoped task type key used when the `taskTypeKey` input is not wired.",
        ),
      taskTypeAnchorEntityType: z
        .nativeEnum(SystemEntity)
        .optional()
        .describe(
          "Copied from the selected task type's declared anchor entity. Drives the anchorEntity input socket (required + typed) so the canvas enforces the type's source-entity constraint. Set by the task-type picker; do not set independently of taskTypeKey.",
        ),
      taskTypeInputFields: z
        .array(
          z.object({
            fieldName: z.string(),
            fieldType: z.string(),
            label: z.string(),
            isRequired: z.boolean(),
            isMultiple: z.boolean(),
          }),
        )
        .optional()
        .describe(
          "Copied from the selected task type's input schema. Drives per-field taskInput.* sockets. Set by the task-type picker; do not set independently of taskTypeKey.",
        ),
      assigneeType: z
        .enum(["user", "agent"])
        .optional()
        .describe("Default assignee type when input assigneeType is not set"),
      assigneeUserId: z
        .uuid()
        .optional()
        .describe("Default user assignee ID when assigneeType is user"),
      assigneeAgentDefinitionId: z
        .uuid()
        .optional()
        .describe(
          "Default agent definition assignee ID when assigneeType is agent",
        ),
      taskNamespaceKey: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe(
          "Namespace key (e.g. 'SHIP') to create the subtask in. Portable across orgs — resolved at run time. Used when the `taskNamespaceId` input is not wired. A selected task type with a live default namespace still wins.",
        ),
      taskNamespaceId: z
        .uuid()
        .optional()
        .describe(
          "Namespace id to create the subtask in. Prefer `taskNamespaceKey` in templates. The input socket overrides this field. A selected task type with a live default namespace still wins.",
        ),
    })
    .superRefine((value, ctx) => {
      if (value.assigneeType === "user" && !value.assigneeUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeUserId"],
          message: "assigneeUserId is required when assigneeType is 'user'",
        });
      }
      if (value.assigneeType === "agent" && !value.assigneeAgentDefinitionId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeAgentDefinitionId"],
          message:
            "assigneeAgentDefinitionId is required when assigneeType is 'agent'",
        });
      }
      if (
        !value.assigneeType &&
        (value.assigneeUserId || value.assigneeAgentDefinitionId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeType"],
          message:
            "assigneeType is required when a default assignee ID is provided",
        });
      }
    }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  // Emitted indirectly: TaskService publishes TASK_CREATED on creation (the
  // single lifecycle emission point).
  emits: [SystemEvent.TASK_CREATED] as const,
});
